import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import Button from 'core/components/button/button';
import { Label } from 'core/components/label';
import { trackAndWait } from 'core/helpers/analytics';
import { getThemeVariant, saveThemeVariant, THEME_VARIANTS } from 'core/components/theme/theme-variant';
import useStyles from './theme-switch-dialog.styles';

export const themeSwitchLabels = {
  [THEME_VARIANTS.LEGACY]: 'Try the new look',
  [THEME_VARIANTS.RESKIN]: 'Switch to the classic look'
};

const RELOAD_CANCELLED_AFTER_MS = 1000;

const ThemeSwitchDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const [switching, setSwitching] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const switchingRef = useRef(false);
  const switchAttemptedRef = useRef(false);

  const themeVariant = getThemeVariant();
  const targetThemeVariant = themeVariant === THEME_VARIANTS.RESKIN ? THEME_VARIANTS.LEGACY : THEME_VARIANTS.RESKIN;
  const label = themeSwitchLabels[themeVariant];

  const switchThemeVariant = async () => {
    if (switchingRef.current) {
      return;
    }
    if (!saveThemeVariant(targetThemeVariant)) {
      setSaveFailed(true);
      return;
    }
    switchingRef.current = true;
    switchAttemptedRef.current = true;
    setSwitching(true);
    try {
      await trackAndWait('Staff Theme Changed', { fromVariant: themeVariant, toVariant: targetThemeVariant });
    } catch {
      // Tracking is best-effort; switch anyway.
    }
    // reload() returns once a leave-page prompt is answered; if the page is still here after that, the user
    // chose to stay, so make the dialog usable again.
    window.location.reload();
    setTimeout(() => {
      switchingRef.current = false;
      setSwitching(false);
    }, RELOAD_CANCELLED_AFTER_MS);
  };

  const close = () => {
    // Backing out keeps the current look on the next page load.
    if (switchAttemptedRef.current) {
      saveThemeVariant(themeVariant);
      switchAttemptedRef.current = false;
    }
    setSaveFailed(false);
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={switching ? undefined : close}
      size="sm"
      className={classes.dialog}
      aria-labelledby="theme-switch-title"
    >
      <div className={classes.content}>
        <Label id="theme-switch-title" type="greenBig" className={classes.title}>
          {label}?
        </Label>
        <Label type="darkGreenMedium" className={classes.message}>
          The page will reload, and any unsaved changes will be lost. You can switch back anytime from this menu.
        </Label>
        {saveFailed && (
          <Label type="darkGreenMedium" className={classes.error} role="alert">
            Your browser blocked saving this setting, so the look didn't change. Allow this site to store data, then try
            again.
          </Label>
        )}
        <Button
          className={classes.confirmButton}
          variant="contained"
          color="primary"
          onClick={switchThemeVariant}
          loading={switching}
          disabled={switching}
        >
          {label}
        </Button>
        <Button variant="text" color="secondary" onClick={close} disabled={switching}>
          Go back
        </Button>
      </div>
    </BaseDialog>
  );
};

ThemeSwitchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ThemeSwitchDialog;
